/**
 * Phase C — Final Report export pipeline.
 *
 * Two responsibilities:
 *   1. `buildDocx(report)` — produce a Word (.docx) file from a structured
 *      FinalReport. Procurement portals accept Word; markdown is a non-starter.
 *   2. `buildReadinessChecks(report)` — return a list of pre-submission
 *      readiness checks (with pass/fail/warn status + a one-line reason)
 *      so the Viewer can gate the Download button until the bidder has
 *      actively waived any failed checks.
 *
 * Both functions are pure data transforms — no React, no DOM access — so
 * they're trivially testable and can be reused by a future server-side
 * exporter (Phase D) without modification.
 */

import {
  AlignmentType,
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  TextRun,
} from 'docx';
import { saveAs } from 'file-saver';
import { FinalReport, FinalReportResponse, ComplianceItem } from '../types';

// ────────────────────────────────────────────────────────────────────────────
// Word (.docx) export
// ────────────────────────────────────────────────────────────────────────────

function _para(text: string, opts: Partial<{ bold: boolean; size: number; italic: boolean; color: string }> = {}): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text,
        bold: opts.bold,
        size: opts.size,
        italics: opts.italic,
        color: opts.color,
      }),
    ],
  });
}

function _heading(text: string, level: typeof HeadingLevel[keyof typeof HeadingLevel]): Paragraph {
  return new Paragraph({
    heading: level,
    children: [new TextRun({ text })],
    spacing: { before: 240, after: 120 },
  });
}

function _spacer(): Paragraph {
  return new Paragraph({ children: [new TextRun({ text: '' })] });
}

function _responseBlock(r: FinalReportResponse, idx: number, sourceLabel: string): Paragraph[] {
  const blocks: Paragraph[] = [
    new Paragraph({
      heading: HeadingLevel.HEADING_3,
      children: [new TextRun({ text: `${idx + 1}. ${r.question}` })],
      spacing: { before: 200, after: 100 },
    }),
    new Paragraph({
      children: [
        new TextRun({ text: sourceLabel, italics: true, color: '6b7280', size: 18 }),
      ],
    }),
  ];

  // Multi-paragraph rendering: split the response on blank lines so Word
  // gets separate paragraphs (rather than one long block of text).
  const bodyParas = (r.response || '').split(/\n\s*\n+/).filter(Boolean);
  for (const p of bodyParas) {
    blocks.push(new Paragraph({ children: [new TextRun({ text: p, size: 22 })] }));
  }

  // Provenance footer: word count, score, KB tier (drafted only), and the
  // top tender-bank match if available.
  const footerBits: string[] = [];
  if (typeof r.word_count === 'number') footerBits.push(`${r.word_count} words`);
  if (typeof r.improvement_score === 'number') footerBits.push(`Score: ${Math.round(r.improvement_score)}/100`);
  if (r.kb_coverage_tier) footerBits.push(`KB grounding: ${r.kb_coverage_tier}`);
  if (r.past_tender_matches && r.past_tender_matches.length) {
    const top = r.past_tender_matches[0];
    let attrib = `Informed by: ${top.tender_title}`;
    if (top.outcome) attrib += ` (${top.outcome}`;
    if (typeof top.tender_score === 'number') attrib += `, ${top.tender_score}`;
    if (top.outcome) attrib += ')';
    footerBits.push(attrib);
  }
  if (footerBits.length) {
    blocks.push(
      new Paragraph({
        children: [new TextRun({ text: footerBits.join(' · '), italics: true, color: '6b7280', size: 18 })],
        spacing: { after: 120 },
      }),
    );
  }
  return blocks;
}

/**
 * Build a structured Word document from the FinalReport. Returns a Blob
 * the caller can pass to `file-saver`'s `saveAs`.
 */
export async function buildDocx(report: FinalReport): Promise<Blob> {
  const children: Paragraph[] = [];

  // Title block
  children.push(
    new Paragraph({
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.LEFT,
      children: [new TextRun({ text: 'Final Tender Submission' })],
    }),
    new Paragraph({
      children: [
        new TextRun({ text: `Date: ${report.date}`, color: '6b7280' }),
      ],
    }),
  );

  if (typeof report.overall_score === 'number') {
    children.push(_para(`Overall score: ${Math.round(report.overall_score)} / ${report.max_score}`, { bold: true }));
  }

  // AI disclosure (PPN-035) — stays prominent.
  if (report.ai_disclosure) {
    children.push(_spacer(), _heading('AI Use Disclosure (PPN-035)', HeadingLevel.HEADING_1));
    const ai = report.ai_disclosure;
    const ungroundedCount = ai.ungrounded_count || 0;
    const groundedDraftedCount = Math.max(0, (ai.drafted_count || 0) - ungroundedCount);
    children.push(_para(`Model: ${ai.model_id}`, { italic: true }));
    children.push(_para(`${ai.refined_count} bidder-authored response(s) refined by AI using the knowledge base.`));
    children.push(_para(`${groundedDraftedCount} response(s) authored from scratch by AI for previously-unanswered questions, grounded in the knowledge base.`));
    if (ungroundedCount > 0) {
      // Surfaced as bold so the legal disclosure is unambiguous about
      // which AI outputs lacked company-specific grounding at draft time.
      children.push(_para(
        `${ungroundedCount} response(s) scaffolded by AI WITHOUT any company-specific evidence (knowledge base was empty at time of generation). These contained bracketed placeholders the bidder filled in manually before submission.`,
        { bold: true }
      ));
    }
    if (ai.original_count) children.push(_para(`${ai.original_count} response(s) are the bidder's original wording, unchanged.`));
    if (ai.excluded_count) children.push(_para(`${ai.excluded_count} question(s) were excluded by the bidder.`));
    children.push(_para('All AI outputs were reviewed by the bidder before submission.'));
  }

  // Submission justification
  if (report.submission_justification) {
    children.push(_spacer(), _heading('Submission Justification', HeadingLevel.HEADING_1));
    const paras = report.submission_justification.split(/\n\s*\n+/).filter(Boolean);
    for (const p of paras) children.push(_para(p));
  }

  // Evaluation criteria
  if (report.evaluation_criteria && report.evaluation_criteria.length) {
    children.push(_spacer(), _heading('Evaluation Criteria', HeadingLevel.HEADING_1));
    for (const c of report.evaluation_criteria) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `${c.name}`, bold: true }),
            new TextRun({
              text:
                (typeof c.weight === 'number' ? ` (${c.weight}%)` : '') +
                (c.priority_level ? ` · ${c.priority_level}` : ''),
              color: '6b7280',
            }),
          ],
          spacing: { before: 120 },
        }),
      );
      if (c.description) children.push(_para(c.description));
    }
  }

  // Refined responses
  if (report.refined_responses && report.refined_responses.length) {
    children.push(_spacer(), _heading('Refined Responses', HeadingLevel.HEADING_1));
    report.refined_responses.forEach((r, i) => {
      children.push(..._responseBlock(r, i, 'Refined from your draft'));
    });
  }

  // Drafted responses
  if (report.drafted_responses && report.drafted_responses.length) {
    children.push(_spacer(), _heading('Drafted Responses (AI-authored from your knowledge base)', HeadingLevel.HEADING_1));
    report.drafted_responses.forEach((r, i) => {
      children.push(..._responseBlock(r, i, 'Drafted by AI from your knowledge base'));
    });
  }

  // Compliance acknowledgements
  if (report.compliance_items && report.compliance_items.length) {
    children.push(_spacer(), _heading('Compliance Acknowledgements', HeadingLevel.HEADING_1));
    children.push(_para('Items below are declarations / signatures / form-fills that must be completed before submission.', { italic: true }));
    report.compliance_items.forEach((ci: ComplianceItem, idx: number) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `[ ] ${idx + 1}. `, bold: true }),
            new TextRun({ text: ci.text || '' }),
            ci.section
              ? new TextRun({ text: ` — ${ci.section}`, italics: true, color: '6b7280' })
              : new TextRun({ text: '' }),
          ],
          spacing: { before: 120 },
        }),
      );
    });
  }

  // Excluded questions (transparency footer)
  if (report.excluded_questions && report.excluded_questions.length) {
    children.push(_spacer(), _heading('Excluded by Bidder', HeadingLevel.HEADING_1));
    children.push(_para('The following questions were extracted from the source document but explicitly excluded by the bidder from this submission.', { italic: true }));
    report.excluded_questions.forEach((q, idx) => {
      children.push(_para(`${idx + 1}. ${q.question}`));
    });
  }

  const doc = new Document({
    creator: 'Open Tender AI',
    description: 'AI-assisted tender submission report',
    title: 'Final Tender Submission',
    sections: [{ properties: {}, children }],
  });

  return await Packer.toBlob(doc);
}

/** Convenience wrapper that triggers a browser download. */
export async function downloadDocx(report: FinalReport): Promise<void> {
  const blob = await buildDocx(report);
  const safeDate = (report.date || new Date().toISOString().slice(0, 10)).replace(/[^0-9-]/g, '');
  saveAs(blob, `tender-submission-${safeDate}.docx`);
}

// ────────────────────────────────────────────────────────────────────────────
// Pre-submission readiness checklist
// ────────────────────────────────────────────────────────────────────────────

export type CheckSeverity = 'pass' | 'warn' | 'fail';

export interface ReadinessCheck {
  id: string;
  label: string;
  severity: CheckSeverity;
  detail: string;
}

/**
 * Compute a list of pre-submission checks against the structured report.
 * The Viewer renders these inline before the user clicks Download / Export.
 *
 * Severity semantics:
 *   - `fail`: hard blocker — would make the submission non-compliant or
 *     embarrassingly incomplete (e.g. drafted answer flagged 'critical').
 *   - `warn`: probably fine but worth a glance (e.g. compliance items present
 *     but unchecked — they're checklist items, not auto-passable).
 *   - `pass`: explicit confirmation — gives the user confidence that the
 *     check ran, didn't just silently elide.
 */
export function buildReadinessChecks(report: FinalReport): ReadinessCheck[] {
  const checks: ReadinessCheck[] = [];

  // Q1 — All extracted questions accounted for (refined + drafted + excluded).
  const refinedN = report.refined_responses?.length || 0;
  const draftedN = report.drafted_responses?.length || 0;
  const excludedN = report.excluded_questions?.length || 0;
  if (refinedN + draftedN === 0) {
    checks.push({
      id: 'has_responses',
      label: 'Responses generated',
      severity: 'fail',
      detail: 'No refined or drafted responses are present. Run the Response Refinement step before submitting.',
    });
  } else {
    checks.push({
      id: 'has_responses',
      label: 'Responses generated',
      severity: 'pass',
      detail: `${refinedN} refined and ${draftedN} drafted response${refinedN + draftedN === 1 ? '' : 's'} included.`,
    });
  }

  // Q2 — KB grounding: drafted answers in 'critical' tier are blockers.
  const ev = report.evidence_summary;
  if (ev && ev.total_drafted > 0) {
    const critical = (ev.tier_counts.critical || 0);
    const poor = (ev.tier_counts.poor || 0);
    if (critical > 0) {
      checks.push({
        id: 'kb_grounding',
        label: 'KB grounding for drafted answers',
        severity: 'fail',
        detail: `${critical} drafted answer${critical === 1 ? '' : 's'} marked 'critical' — these will read as generic. Upload supporting docs to your KB first.`,
      });
    } else if (poor > 0) {
      checks.push({
        id: 'kb_grounding',
        label: 'KB grounding for drafted answers',
        severity: 'warn',
        detail: `${poor} drafted answer${poor === 1 ? '' : 's'} marked 'poor'. Review before submitting.`,
      });
    } else {
      checks.push({
        id: 'kb_grounding',
        label: 'KB grounding for drafted answers',
        severity: 'pass',
        detail: 'All drafted answers have fair-or-better KB coverage.',
      });
    }
  }

  // Q3 — Compliance items: they need explicit human acknowledgement, so any
  // compliance items present without checked state surface as warnings.
  const compN = report.compliance_items?.length || 0;
  if (compN > 0) {
    checks.push({
      id: 'compliance_items',
      label: 'Compliance acknowledgements',
      severity: 'warn',
      detail: `${compN} compliance item${compN === 1 ? '' : 's'} (declarations / signatures) require manual sign-off in the exported file.`,
    });
  }

  // Q4 — AI disclosure block present (PPN-035).
  if (report.ai_disclosure) {
    checks.push({
      id: 'ai_disclosure',
      label: 'AI use disclosure included',
      severity: 'pass',
      detail: 'PPN-035 disclosure paragraph is included in the export.',
    });
  } else {
    checks.push({
      id: 'ai_disclosure',
      label: 'AI use disclosure included',
      severity: 'warn',
      detail: 'No AI disclosure block was generated for this run. Add one before submitting if AI was used.',
    });
  }

  // Q5 — Word counts: any answer way longer than its suggested length.
  const allResponses = [...(report.refined_responses || []), ...(report.drafted_responses || [])];
  const overlong = allResponses.filter(
    (r) => typeof r.suggested_words === 'number' && typeof r.word_count === 'number' && r.word_count > r.suggested_words * 1.5,
  );
  if (overlong.length > 0) {
    checks.push({
      id: 'word_counts',
      label: 'Word counts within target',
      severity: 'warn',
      detail: `${overlong.length} response${overlong.length === 1 ? ' is' : 's are'} more than 50% over the suggested word count. Review before submitting.`,
    });
  }

  // Excluded summary — informational only.
  if (excludedN > 0) {
    checks.push({
      id: 'excluded_questions',
      label: 'Excluded questions noted',
      severity: 'pass',
      detail: `${excludedN} question${excludedN === 1 ? '' : 's'} excluded by you — listed in the export's transparency footer.`,
    });
  }

  return checks;
}

/** True if any check is a hard fail. */
export function hasBlockingFailures(checks: ReadinessCheck[]): boolean {
  return checks.some((c) => c.severity === 'fail');
}
